import type { TranslationTag } from '@ps/web-core-intl';
import { get, set } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

type TooltipElement = Element | null;

export type SetFlowToStep = React.Dispatch<React.SetStateAction<TooltipElement>> | null;

export interface OnboardingInfo<TContext> {
  id: string;
  heading?: JSX.Element;
  context: TContext;
  placement: string;
}

interface OnboardingContextType {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  refEl: TooltipElement;
  setReferenceElement: React.Dispatch<React.SetStateAction<TooltipElement>>;
  setFlowToStep: (step: number) => SetFlowToStep;
  onboardingFinished: boolean;
  steps: OnboardingInfo<JSX.Element | TranslationTag>[];
  backdropRefEl: HTMLElement | null;
  setBackdropRefEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  handleClose?: (callback?: () => void) => void;
  handleBack?: () => void;
  handleNext?: (callback?: () => void) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboardingContext = (): OnboardingContextType =>
  useContext(OnboardingContext) as OnboardingContextType;

export const OnboardingProvider = ({
  children,
  steps,
  storagePath
}: {
  children: React.ReactNode;
  steps: OnboardingInfo<JSX.Element | TranslationTag>[];
  storagePath: string;
}) => {
  const { localSettings } = useDispatch<Dispatch>();
  const settings = useSelector(select.localSettings.get);
  const [refEl, setReferenceElement] = useState<TooltipElement>(null);
  const [backdropRefEl, setBackdropRefEl] = useState<HTMLElement | null>(null);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [onboardingFinished, setOnboardingFinished] = useState<boolean>(false);
  const setFlowToStep = useCallback(
    (step: number) => (activeStep === step ? setReferenceElement : null),
    [activeStep]
  );

  const onboardingModalShown = get(settings, `${storagePath}.onboardingModalShown`, false);

  const onboardingModalFinished = get(settings, `${storagePath}.onboardingFinished`, false);

  const handleClose = useCallback(
    (fallback?: () => void) => {
      const localSettingDynamic = {};
      set(localSettingDynamic, `${storagePath}.onboardingFinished`, true);
      set(localSettingDynamic, `${storagePath}.onboardingModalShown`, true);

      localSettings.set(localSettingDynamic);
      fallback?.();
    },
    [localSettings, storagePath]
  );

  const handleBack = useCallback(() => {
    if (activeStep !== 0) {
      setActiveStep((activeStep) => activeStep - 1);
    }
  }, [activeStep]);

  const handleNext = useCallback(
    (fallback?: () => void) => {
      if (activeStep < steps.length - 1) {
        setActiveStep((activeStep) => activeStep + 1);
      } else {
        fallback?.();
      }
    },
    [activeStep, steps.length]
  );

  useEffect(
    () =>
      setOnboardingFinished(
        (onboardingModalFinished && onboardingModalShown) || !onboardingModalShown
      ),
    [onboardingModalFinished, onboardingModalShown]
  );

  const contextProperties = useMemo(
    () => ({
      refEl,
      setReferenceElement,
      activeStep,
      setActiveStep,
      setFlowToStep,
      onboardingFinished,
      handleClose,
      handleBack,
      handleNext,
      steps,
      backdropRefEl,
      setBackdropRefEl
    }),
    [
      activeStep,
      onboardingFinished,
      refEl,
      setFlowToStep,
      steps,
      handleClose,
      handleBack,
      handleNext,
      backdropRefEl,
      setBackdropRefEl
    ]
  );
  return (
    <OnboardingContext.Provider value={contextProperties}>{children}</OnboardingContext.Provider>
  );
};
